import type {
  Command,
  GameEvent,
  MatchSummary,
  PlayerId,
  PlayerView,
  ServerMessage,
  SocketAuthMessage,
} from '@seven-dawns/shared';
import type { JoinRequestStatus } from './api';

/**
 * Server messages that are not (yet) part of the shared `ServerMessage` union:
 * - `lobby`: sent instead of `view` while the match is in the lobby and no view can be built.
 * - `joinRequest`: sent on the join-request socket (authenticated with a request token).
 */
export type LobbyMessage = { t: 'lobby'; summary: MatchSummary; you: PlayerId };
export type JoinRequestMessage = { t: 'joinRequest' } & JoinRequestStatus;
export type IncomingMessage = ServerMessage | LobbyMessage;

export type ConnectionStatus = 'idle' | 'connecting' | 'open' | 'reconnecting' | 'closed';

export interface GameSocketHandlers {
  onView(view: PlayerView): void;
  onEvents(events: GameEvent[]): void;
  /** Lobby summary, sent instead of a view while the match is in the lobby. */
  onLobby?(summary: MatchSummary, you: PlayerId): void;
  onStatus(status: ConnectionStatus): void;
  /** Server-reported clock skew, so countdowns stay accurate. */
  onClockOffset?(offsetMs: number): void;
  /** Fatal errors (e.g. token rejected); the socket stops reconnecting. */
  onFatal?(error: string): void;
}

export type CommandAck = { ok: true; eta?: number } | { ok: false; error: string };

interface PendingCommand {
  resolve(ack: CommandAck): void;
  timer: ReturnType<typeof setTimeout>;
}

const HEARTBEAT_MS = 25_000;
const COMMAND_TIMEOUT_MS = 15_000;
const MAX_BACKOFF_MS = 30_000;

/** Factory so tests can inject a fake WebSocket. */
export type SocketFactory = (url: string) => WebSocket;

/**
 * Resilient WebSocket connection to a match: authenticates with its first message (the token
 * never goes in the URL), reconnects with jittered exponential backoff, sends heartbeats,
 * reconnects promptly when the tab becomes visible or the network returns, and correlates
 * command acknowledgements.
 */
export class GameSocket {
  private ws: WebSocket | null = null;
  private status: ConnectionStatus = 'idle';
  private attempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private pending = new Map<string, PendingCommand>();
  private nextId = 1;
  private stopped = false;
  private pingSentAt = 0;

  constructor(
    private readonly url: string,
    private readonly handlers: GameSocketHandlers,
    private readonly createSocket: SocketFactory = (u) => new WebSocket(u),
    /** Sent first on every (re)connection. */
    private readonly auth?: SocketAuthMessage,
  ) {}

  connect(): void {
    this.stopped = false;
    this.open();
    if (typeof window !== 'undefined') {
      window.addEventListener('online', this.handleWake);
      document.addEventListener('visibilitychange', this.handleWake);
    }
  }

  close(): void {
    this.stopped = true;
    this.clearTimers();
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', this.handleWake);
      document.removeEventListener('visibilitychange', this.handleWake);
    }
    this.ws?.close(1000, 'client closing');
    this.ws = null;
    this.failPending('Disconnected');
    this.setStatus('closed');
  }

  /** Send a command and resolve when the server acknowledges it (or it times out). */
  send(cmd: Command): Promise<CommandAck> {
    const id = `c${this.nextId++}`;
    return new Promise<CommandAck>((resolve) => {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
        resolve({ ok: false, error: 'You are offline. Your order was not sent.' });
        return;
      }
      const timer = setTimeout(() => {
        this.pending.delete(id);
        resolve({ ok: false, error: 'The server did not respond. Please try again.' });
      }, COMMAND_TIMEOUT_MS);
      this.pending.set(id, { resolve, timer });
      this.ws.send(JSON.stringify({ t: 'cmd', id, cmd }));
    });
  }

  get currentStatus(): ConnectionStatus {
    return this.status;
  }

  private open(): void {
    if (this.stopped) return;
    this.setStatus(this.attempt === 0 ? 'connecting' : 'reconnecting');
    const ws = this.createSocket(this.url);
    this.ws = ws;

    ws.onopen = () => {
      if (this.auth) ws.send(JSON.stringify(this.auth));
      this.attempt = 0;
      this.setStatus('open');
      this.startHeartbeat();
    };
    ws.onmessage = (event: MessageEvent) => {
      if (typeof event.data === 'string') this.handleMessage(event.data);
    };
    ws.onclose = (event: CloseEvent) => {
      if (this.ws !== ws) return;
      this.ws = null;
      this.clearTimers();
      this.failPending('Connection lost. Your order may not have been received.');
      // 4401/4403: token invalid or removed from match — don't hammer the server.
      if (event.code === 4401 || event.code === 4403) {
        this.stopped = true;
        this.setStatus('closed');
        this.handlers.onFatal?.(event.reason || 'You no longer have access to this match.');
        return;
      }
      this.scheduleReconnect();
    };
    ws.onerror = () => {
      // onclose follows and handles reconnection.
    };
  }

  private handleMessage(raw: string): void {
    let msg: IncomingMessage;
    try {
      msg = JSON.parse(raw) as IncomingMessage;
    } catch {
      return;
    }
    switch (msg.t) {
      case 'view':
        this.handlers.onView(msg.view);
        break;
      case 'events':
        this.handlers.onEvents(msg.events);
        break;
      case 'lobby':
        this.handlers.onLobby?.(msg.summary, msg.you);
        break;
      case 'ack': {
        const pending = this.pending.get(msg.id);
        if (!pending) return;
        clearTimeout(pending.timer);
        this.pending.delete(msg.id);
        pending.resolve(msg.ok ? { ok: true, eta: msg.eta } : { ok: false, error: msg.error });
        break;
      }
      case 'pong': {
        if (this.pingSentAt > 0) {
          const rtt = Date.now() - this.pingSentAt;
          this.handlers.onClockOffset?.(msg.serverTimeMs + rtt / 2 - Date.now());
        }
        break;
      }
      case 'error':
        console.warn('Server error:', msg.error);
        break;
      default:
        // Unknown message types are ignored so older clients keep working.
        break;
    }
  }

  private startHeartbeat(): void {
    this.sendPing();
    this.heartbeatTimer = setInterval(() => this.sendPing(), HEARTBEAT_MS);
  }

  private sendPing(): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.pingSentAt = Date.now();
      this.ws.send(JSON.stringify({ t: 'ping' }));
    }
  }

  private scheduleReconnect(): void {
    if (this.stopped) return;
    this.attempt += 1;
    this.setStatus('reconnecting');
    const base = Math.min(MAX_BACKOFF_MS, 500 * 2 ** Math.min(this.attempt, 6));
    const delay = base / 2 + Math.random() * (base / 2);
    this.reconnectTimer = setTimeout(() => this.open(), delay);
  }

  private readonly handleWake = (): void => {
    if (this.stopped || document.visibilityState === 'hidden') return;
    if (this.status === 'reconnecting' && this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
      this.open();
    }
  };

  private setStatus(status: ConnectionStatus): void {
    if (this.status === status) return;
    this.status = status;
    this.handlers.onStatus(status);
  }

  private clearTimers(): void {
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    if (this.heartbeatTimer) clearInterval(this.heartbeatTimer);
    this.reconnectTimer = null;
    this.heartbeatTimer = null;
  }

  private failPending(error: string): void {
    for (const [id, p] of this.pending) {
      clearTimeout(p.timer);
      p.resolve({ ok: false, error });
      this.pending.delete(id);
    }
  }
}

/** WebSocket URL for the current origin. Credentials go in the first message, never here. */
export function socketUrl(): string {
  const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${location.host}/ws`;
}

/**
 * The join-request socket: authenticates with the request token as its first message, then
 * reports each `joinRequest` status. A 4403 close means the request was declined.
 */
export function openRequestSocket(
  url: string,
  requestToken: string,
  onStatus: (status: JoinRequestStatus) => void,
  createSocket: SocketFactory = (u) => new WebSocket(u),
): WebSocket {
  const ws = createSocket(url);
  ws.onopen = () => ws.send(JSON.stringify({ t: 'auth', requestToken } satisfies SocketAuthMessage));
  ws.onmessage = (event: MessageEvent) => {
    if (typeof event.data !== 'string') return;
    try {
      const msg = JSON.parse(event.data) as JoinRequestMessage;
      if (msg.t === 'joinRequest') {
        const { t: _t, ...status } = msg;
        onStatus(status);
      }
    } catch {
      // Ignore malformed frames; the caller keeps polling.
    }
  };
  ws.onclose = (event: CloseEvent) => {
    if (event.code === 4403) onStatus({ status: 'rejected' });
  };
  return ws;
}
